import React from 'react';
import styled from 'styled-components';
import { Modal } from '/imports/ui/Modal';
import { Button } from '/imports/ui/Button';
import { Loading } from '/imports/ui/Loading';
import { strings } from '/imports/strings';
import { FootprintEnrichment } from '/imports/api/buildingFootprints';

const FootprintDetailModalStyled = styled(Modal)`
  .modal-title {
    font-size: 1.5rem;
  }
  .modal-window {
    min-width: 26rem;
    max-width: 48rem;
  }
  .detail-row {
    display: flex;
    gap: 0.5rem;
    margin: 0.5rem 0;
    label {
      font-weight: bold;
    }
  }
  .full-details {
    max-height: 60vh;
    overflow-y: auto;
    .detail-row {
      margin: 0.25rem 0;
      label {
        min-width: 18rem;
      }
      span {
        word-break: break-word;
      }
    }
  }
  .modal-buttons {
    justify-content: flex-end;
  }
  .loading-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
  }
`;

export type FootprintModalState = 'idle' | 'enriching' | 'enriched';

interface Props {
  state: FootprintModalState;
  enrichment?: FootprintEnrichment;
  area?: number;
  fullDetails?: { label: string, value: any }[];
  onAdd: () => void;
  onEnrich: () => void;
  onRemove: () => void;
  onClose: () => void;
}

/**
 * Renders a detail value for display, normalizing empties, booleans, and numbers.
 * @param value - The raw value to format.
 * @returns A display string.
 */
const formatValue = (value: any) => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return value.toLocaleString();
  return `${value}`;
};

/** Modal showing a building footprint's enrichment details with add/enrich/remove actions. */
export const FootprintDetailModal = (props: Props) => {
  const { state, enrichment, area, fullDetails, onAdd, onEnrich, onRemove, onClose } = props;
  /** Renders the modal body for the current state (loading, enriched details, or summary). */
  const renderBody = () => {
    if (state === 'enriching') return <div className="loading-wrap"><Loading /></div>;
    if (state === 'enriched') {
      return (
        <div className="full-details">
          {(fullDetails || []).map((row) => (
            <div key={row.label} className="detail-row">
              <label>{row.label}:</label>
              <span>{formatValue(row.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    if (!enrichment) return <div className="loading-wrap"><Loading /></div>;
    return (
      <>
        <div className="detail-row">
          <label>{strings.Address()}:</label>
          <span>{enrichment.addressString}</span>
        </div>
        {area !== undefined && (
          <div className="detail-row">
            <label>{strings.FootprintArea()}:</label>
            <span>{Math.round(area).toLocaleString()} sq.ft.</span>
          </div>
        )}
      </>
    );
  };
  /** Renders the footer action buttons appropriate to the current state. */
  const renderButtons = () => {
    if (state === 'enriching') return null;
    if (state === 'enriched') {
      return (
        <>
          <Button className="primary" onClick={onClose}>{strings.Keep()}</Button>
          <Button className="secondary" onClick={onRemove}>{strings.Remove()}</Button>
        </>
      );
    }
    return (
      <>
        <Button className="primary" onClick={onAdd} disabled={!enrichment}>{strings.AddToBatch()}</Button>
        <Button className="primary" onClick={onEnrich} disabled={!enrichment}>{strings.Enrich()}</Button>
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
      </>
    );
  };
  return (
    <FootprintDetailModalStyled title={strings.FootprintDetails()} onClose={onClose}>
      {renderBody()}
      <div className="modal-buttons">{renderButtons()}</div>
    </FootprintDetailModalStyled>
  );
};
